import React from "react";
import { MoreVert } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../Components/UserContext";
import {
  Box,
  Typography,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Skeleton,
  IconButton,
  useMediaQuery
} from "@mui/material";

import { styled, Theme, CSSObject } from '@mui/material/styles';
import dacapi from "../Components/Assets/macio.png";
import logo_new from "../Components/Assets/logo_new.png";
import FeedbackIcon from "@mui/icons-material/Feedback";
// Theme switcher icons removed - app is always dark mode

import useSignOut from "react-auth-kit/hooks/useSignOut";
import Menu from '@mui/icons-material/Menu';

import { useSpring, animated } from '@react-spring/web';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import MapIcon from '@mui/icons-material/Map';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import ExploreIcon from '@mui/icons-material/Explore';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationBell from '../Components/NotificationBell/NotificationBell';
import ScienceIcon from '@mui/icons-material/Science';

const drawerWidth = 265;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});



const DrawerStyled = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...openedMixin(theme),
  '& .MuiDrawer-paper': {
    ...openedMixin(theme),
    width: drawerWidth,
    overflowX: 'hidden',
  },
}));

const Sidebar: React.FC<{ children: React.ReactNode, handleOpenFeedback: () => void }> = ({ children, handleOpenFeedback }) => {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleSignOut = () => {
    signOut();
    localStorage.removeItem("role");
    navigate("/login");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };



  const [feedbackHovered, setFeedbackHovered] = React.useState(false);
  const feedbackHoverStyle = useSpring({
    transform: feedbackHovered ? 'scale(1.05)' : 'scale(1)',
  });

  // Theme hover removed - no theme switcher

  const [bellHovered, setBellHovered] = React.useState(false);
  
  const commonDrawerContent = (
    <>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/">
          <img src={logo_new} style={{ width: '100%' }} alt="DACAPI" />
        </Link>
      </Box>
      <List sx={{ 
        flexGrow: 1,
        width: '100%',
        px: 0,
      }}>
        {children}
      </List>
      <Box sx={{ mb: 2, pr: 2, textAlign: 'right', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        {localStorage.getItem("role") !== "instalator" && (
          <animated.div style={useSpring({
            transform: bellHovered ? 'scale(1.05)' : 'scale(1)',
          })}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer' }}
              onMouseEnter={() => setBellHovered(true)}
              onMouseLeave={() => setBellHovered(false)}
              onClick={() => navigate('/notifications')}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  width: '100%',
                  cursor: 'pointer'
                }}
              >
                <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                  Notifications
                </Typography>
                <NotificationBell className="notification-bell-button" />
              </Box>
            </Box>
          </animated.div>
        )}

        <animated.div style={feedbackHoverStyle}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer' }}
            onMouseEnter={() => setFeedbackHovered(true)}
            onMouseLeave={() => setFeedbackHovered(false)}
            onClick={handleOpenFeedback}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              Send feedback
            </Typography>
            <IconButton
              color="primary"
              sx={{ fontSize: '100%' }}
            >
              <FeedbackIcon sx={{ fontSize: '150%' }} />
            </IconButton>
          </Box>
        </animated.div>

        {/* Theme switcher removed - app is always dark mode */}
      </Box>

      {user && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, display: 'flex', alignItems: 'center' }}>
          <Link to="/profile">
            <Avatar src={user.profile_image_path || "https://beforeigosolutions.com/wp-content/uploads/2021/12/dummy-profile-pic-300x300-1.png"} alt="User Profile" />
          </Link>
          <Box sx={{ ml: 2, flexGrow: 1 }}>
            <Typography variant="body1" fontWeight="bold">{user.username}</Typography>
            <Typography variant="body2" color="text.primary">{user.role}</Typography>
          </Box>
          <Link to="/profile">
            <MoreVert />
          </Link>
        </Box>
      )}
    </>
  );

  if (loading && !isMobile) {
    return (
      <DrawerStyled variant="permanent">
        <Divider />
        <Box sx={{ width: drawerWidth, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper', boxShadow: 1 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="rectangular" width={drawerWidth - 32} height={40} />
          </Box>
          <List sx={{ flexGrow: 1, px: 2 }}>
            {Array.from(new Array(5)).map((_, index) => (
              <ListItemButton key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ marginRight: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={40} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ ml: 2, flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
            </Box>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
        </Box>
      </DrawerStyled>
    );
  }

  if (!user && !isMobile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Box textAlign="center">
          <Typography variant="h6">User not found</Typography>
          <Button variant="contained" color="primary" onClick={handleSignOut} style={{ marginTop: '1rem' }}>
            Log out
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {isMobile ? (
        <>
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', p: 1, boxShadow: 1 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <Menu />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              DACAPI
            </Typography>
          </Box>
          
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: drawerWidth,
                },
              }}
            >
              {commonDrawerContent}
            </Drawer>
          
        </>
      ) : (
        <DrawerStyled variant="permanent">
          {commonDrawerContent}
        </DrawerStyled>
      )}
    </Box>
  );
};

export default Sidebar;

interface SubItem {
  text: string;
  path: string;
  active: boolean;
  icon: React.ReactNode;
}

export interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  active: boolean;
  alert?: boolean;
  onClick?: () => void;
  subItems?: SubItem[];
}

export const SidebarItem: React.FC<SidebarItemProps> = ({ icon, text, active, alert, onClick, subItems }) => {
  const [hovered, setHovered] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  
  const handleClick = () => {
    if (subItems) {
      setOpen(!open);
    }
    onClick?.();
  };

  React.useEffect(() => {
    setHovered(false);
  }, [active]);

  const hoverStyle = useSpring({
    scale: hovered ? 1.01 : 1,
    x: hovered ? 4 : 0,
    config: {
      mass: 1,
      tension: 400,
      friction: 26,
      duration: 150
    }
  });

  return (
    <>
      <animated.div style={{ ...hoverStyle, width: '100%' }}>
        <ListItemButton
          selected={active}
          onClick={handleClick}
          sx={{ 
            position: 'relative',
            mb: 0.5,
            width: '100%',
            boxSizing: 'border-box',
            px: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              width: '4px',
              height: '100%',
              backgroundColor: 'primary.main',
              transform: active ? 'scaleY(1)' : 'scaleY(0)',
              transition: 'transform 0.15s ease-in-out',
            },
            '&:hover': {
              backgroundColor: 'action.hover',
              '& .MuiListItemIcon-root': {
                transform: 'scale(1.1)',
              }
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
              }
            },
            '& .MuiListItemIcon-root': {
              minWidth: 40,
              transition: 'transform 0.2s ease-out',
            },
            '& .MuiListItemText-primary': {
              fontWeight: active ? 'bold' : 'normal',
              transition: 'font-weight 0.15s ease-in-out',
            }
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
          <ListItemText primary={text} />
          {subItems && (
            open ? <ExpandLess /> : <ExpandMore />
          )}
          {alert && (
            <Box 
              sx={{ 
                position: 'absolute', 
                right: 16, 
                width: 8, 
                height: 8, 
                bgcolor: 'secondary.main', 
                borderRadius: '50%',
                transition: 'transform 0.2s ease-out',
                transform: hovered ? 'scale(1.2)' : 'scale(1)',
              }} 
            />
          )}
        </ListItemButton>
      </animated.div>
      {subItems && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems.map((item, index) => (
              <Link to={item.path} key={index}>
                <ListItemButton
                  sx={{
                    pl: 4,
                    py: 0.5,
                    minHeight: 40,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      '& .MuiListItemIcon-root': {
                        transform: 'scale(1.1)',
                      }
                    },
                  }}
                  selected={item.active}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{ 
                      fontSize: '0.9rem'
                    }}
                  />
                </ListItemButton>
              </Link>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};
